/**
 * Sound Player Utility
 *
 * Provides playBuiltinSound and playCustomAudio used by both
 * the Settings page (preview) and ArcRealtimeProvider (live alerts).
 */

import type { BuiltinSoundId } from '@/stores/settings-store';

let _sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!_sharedCtx || _sharedCtx.state === 'closed') {
    _sharedCtx = new AudioContext();
  }
  return _sharedCtx;
}

type OscType = OscillatorType;

function tone(
  ctx: AudioContext,
  freq: number,
  startSec: number,
  durationSec: number,
  peakVolume: number,
  type: OscType = 'sine',
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startSec);
  gain.gain.setValueAtTime(0, startSec);
  gain.gain.linearRampToValueAtTime(peakVolume, startSec + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, startSec + durationSec);
  osc.start(startSec);
  osc.stop(startSec + durationSec);
}

/** Play a frequency sweep (linear ramp from f0 → f1) for sirens. */
function sweep(
  ctx: AudioContext,
  f0: number,
  f1: number,
  startSec: number,
  durationSec: number,
  peakVolume: number,
  type: OscType = 'sine',
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = type;
  osc.frequency.setValueAtTime(f0, startSec);
  osc.frequency.linearRampToValueAtTime(f1, startSec + durationSec);
  gain.gain.setValueAtTime(0, startSec);
  gain.gain.linearRampToValueAtTime(peakVolume, startSec + 0.02);
  gain.gain.linearRampToValueAtTime(peakVolume, startSec + durationSec - 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, startSec + durationSec);
  osc.start(startSec);
  osc.stop(startSec + durationSec);
}

/**
 * Play a synthesized built-in sound.
 * @param id   - one of the BuiltinSoundId values
 * @param vol  - master volume 0–1
 */
export function playBuiltinSound(id: BuiltinSoundId, vol: number): void {
  if (id === 'silent') return;

  try {
    const ctx = getCtx();
    const v = Math.max(0, Math.min(1, vol));
    const now = ctx.currentTime;

    switch (id) {
      // ── default: 2 medium beeps at 880 Hz ────────────────
      case 'default':
        tone(ctx, 880, now,       0.22, v * 0.55);
        tone(ctx, 880, now + 0.32, 0.22, v * 0.55);
        break;

      // ── beep: 1 short sharp beep ──────────────────────────
      case 'beep':
        tone(ctx, 1000, now, 0.14, v * 0.5);
        break;

      // ── chime: descending 880→660→440 (confirmation) ─────
      case 'chime':
        tone(ctx, 880, now,       0.18, v * 0.45);
        tone(ctx, 660, now + 0.22, 0.18, v * 0.40);
        tone(ctx, 440, now + 0.44, 0.28, v * 0.30);
        break;

      // ── klaxon: 3 urgent beeps at 1200 Hz ─────────────────
      case 'klaxon':
        tone(ctx, 1200, now,        0.13, v * 0.70, 'square');
        tone(ctx, 1200, now + 0.18, 0.13, v * 0.70, 'square');
        tone(ctx, 1200, now + 0.36, 0.13, v * 0.70, 'square');
        break;

      // ── pulse: 2 slow low pulses ──────────────────────────
      case 'pulse':
        tone(ctx, 440, now,       0.35, v * 0.45);
        tone(ctx, 440, now + 0.50, 0.35, v * 0.45);
        break;

      // ── siren: 2 sweeping cycles 600↔1200 Hz (police) ────
      case 'siren':
        sweep(ctx,  600, 1200, now,        0.45, v * 0.55, 'sawtooth');
        sweep(ctx, 1200,  600, now + 0.45, 0.45, v * 0.55, 'sawtooth');
        sweep(ctx,  600, 1200, now + 0.90, 0.45, v * 0.55, 'sawtooth');
        break;

      // ── whoop: emergency whoop (down then up) ─────────────
      case 'whoop':
        sweep(ctx, 1000,  400, now,        0.35, v * 0.60, 'sine');
        sweep(ctx,  400, 1000, now + 0.35, 0.35, v * 0.60, 'sine');
        sweep(ctx, 1000,  400, now + 0.70, 0.35, v * 0.60, 'sine');
        break;

      // ── alert: 4 fast sharp beeps ─────────────────────────
      case 'alert':
        tone(ctx, 1500, now,        0.10, v * 0.65, 'square');
        tone(ctx, 1500, now + 0.14, 0.10, v * 0.65, 'square');
        tone(ctx, 1500, now + 0.28, 0.10, v * 0.65, 'square');
        tone(ctx, 1500, now + 0.42, 0.10, v * 0.65, 'square');
        break;

      // ── buzzer: harsh sustained sawtooth buzz ─────────────
      case 'buzzer':
        tone(ctx, 220, now,        0.45, v * 0.50, 'sawtooth');
        tone(ctx, 220, now + 0.55, 0.45, v * 0.50, 'sawtooth');
        break;

      // ── ding_dong: classic 2-tone doorbell ────────────────
      case 'ding_dong':
        tone(ctx, 660, now,        0.50, v * 0.45, 'sine');
        tone(ctx, 524, now + 0.50, 0.70, v * 0.45, 'sine');
        break;

      // ── urgent: 3 ascending sharp tones ───────────────────
      case 'urgent':
        tone(ctx,  800, now,        0.18, v * 0.60, 'square');
        tone(ctx, 1100, now + 0.20, 0.18, v * 0.60, 'square');
        tone(ctx, 1500, now + 0.40, 0.30, v * 0.65, 'square');
        break;

      // ── radar: single quick high ping ─────────────────────
      case 'radar':
        tone(ctx, 2000, now, 0.08, v * 0.55, 'sine');
        tone(ctx, 1500, now + 0.02, 0.18, v * 0.30, 'sine');
        break;

      // ── tritone: 3 ascending mellow tones (NBC-style) ─────
      case 'tritone':
        tone(ctx, 524, now,        0.30, v * 0.45);
        tone(ctx, 660, now + 0.32, 0.30, v * 0.45);
        tone(ctx, 784, now + 0.64, 0.45, v * 0.45);
        break;

      // ── bell: bright bell using stacked harmonics ─────────
      case 'bell': {
        const fundamentals = [880, 1320, 1760];
        const peaks =        [0.45, 0.20, 0.12];
        fundamentals.forEach((f, i) => tone(ctx, f, now, 0.90, v * peaks[i], 'sine'));
        break;
      }

      // ── fire_bell: rapid repeated bell strikes ────────────
      case 'fire_bell':
        for (let i = 0; i < 6; i++) {
          const t = now + i * 0.13;
          tone(ctx, 1200, t, 0.10, v * 0.55, 'triangle');
          tone(ctx, 1800, t, 0.10, v * 0.30, 'sine');
        }
        break;

      // ── voice_placeholder: just a beep until TTS is ready ─
      case 'voice_placeholder':
        tone(ctx, 800, now, 0.20, v * 0.4);
        break;
    }
  } catch {
    // AudioContext not available or blocked by browser policy
  }
}

/**
 * Play a custom audio file stored as a base64 data URL.
 * @param dataUrl - "data:audio/mp3;base64,..." returned by FileReader.readAsDataURL
 * @param vol     - volume 0–1
 */
export function playCustomAudio(dataUrl: string, vol: number): void {
  try {
    const audio = new Audio(dataUrl);
    audio.volume = Math.max(0, Math.min(1, vol));
    audio.play().catch(() => {
      // autoplay blocked — user needs to interact with the page first
    });
  } catch {
    // Audio not supported
  }
}

// ── AudioContext Pre-warm ─────────────────────────────────────────────────────
// Modern browsers suspend a new AudioContext until the user interacts with the
// page (autoplay policy). Without pre-warming, the first resume() call on an
// actual alert can take 1-3 s, creating a noticeable delay.
// We listen for the first pointer or keyboard event (both count as user gestures)
// and immediately resume the shared context so it is ready when an alert fires.

function _prewarmAudioContext(): void {
  try {
    const ctx = getCtx();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* ignore — next play will retry */ });
    }
  } catch { /* AudioContext not supported */ }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', _prewarmAudioContext, { once: true, passive: true });
  window.addEventListener('keydown',     _prewarmAudioContext, { once: true, passive: true });
}
